import {
  Map,
  AdvancedMarker,
  APIProvider,
  useMap
} from '@vis.gl/react-google-maps'
import env from 'shop/env'
import { useAppContent, useGoogleApis, useImages } from 'shop/hooks'
import styled, { CSSObject } from '@emotion/styled'
import { useContext, useEffect, useState } from 'react'
import { LandingContext } from '../Landing/LandingContext'
import { PartnerStore } from '../Landing/types'
import { getEnabledFulfillmentTypes } from '../Landing/utils'
import { ImageTagType } from 'shop/types'
import { StoreIcon, StoreMarker, UserLocationMarker } from './commonStyles'
import ThreeDotsLoader from '../Loader/ThreeDotsLoader'

const DEFAULT_MAP_ZOOM = 8

const BrowseStoresMap = () => {
  const { currentLocation, sortedPartnerStores } = useContext(LandingContext)
  const { areGoogleApisLoaded } = useGoogleApis()
  const firstStore = sortedPartnerStores[0]?.store
  const { coordinates } = firstStore?.address || {}

  const defaultCenter =
    currentLocation?.latLng || coordinates || env.DEFAULT_MAP_CENTER

  if (!areGoogleApisLoaded) {
    return (
      <Root>
        <ThreeDotsLoader size={16} />
      </Root>
    )
  }

  return (
    <Root data-testid='browse-stores-map-root'>
      <APIProvider apiKey={env.MAPS_API_KEY}>
        <Map
          mapId={'4fe2e51066302762793a9398'}
          defaultCenter={defaultCenter}
          defaultZoom={DEFAULT_MAP_ZOOM}
          disableDefaultUI={true}
          mapTypeControl={false}
          streetViewControl={false}
          zoomControl={true}
          zoomControlOptions={{
            position: google.maps.ControlPosition.RIGHT_CENTER
          }}
          fullscreenControl={false}
          clickableIcons={false}
          scrollwheel={true}
        >
          <StoreMarkers />
        </Map>
      </APIProvider>
    </Root>
  )
}

const StoreMarkers = () => {
  const { altLogoImage } = useAppContent()
  const map = useMap()
  const {
    currentLocation,
    sortedPartnerStores,
    mapInteracts,
    setMapInteracts
  } = useContext(LandingContext)

  const { getImage } = useImages(
    [{ imageUrl: altLogoImage }],
    ImageTagType.Standard
  )

  const [hasMapLoaded, setHasMapLoaded] = useState(false)

  // Filtered list of partnerStores ready for display as Map Markers
  const storeMarkers = sortedPartnerStores.filter(({ store }) => {
    const { isDeliveryEnabled, isPickupEnabled } = getEnabledFulfillmentTypes(
      store.orderFulfillmentTypes
    )
    // filter out stores that have no Fulfillment Options
    if (!isDeliveryEnabled && !isPickupEnabled) return null
    const { coordinates } = store.address || {}
    // filter out stores that have no coordinates Lat Lng
    if (!coordinates.lat || !coordinates.lng) return null
    return store
  })

  // Pan to user's location any time it changes
  useEffect(() => {
    if (map && currentLocation?.latLng && hasMapLoaded) {
      map.panTo(currentLocation.latLng)
      const mapZoom = map.getZoom()
      const mapShouldZoom = mapZoom !== 12
      if (mapShouldZoom) map.setZoom(12)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, currentLocation?.latLng])

  const handleStoreClick = (store: PartnerStore) => {
    setMapInteracts({ ...mapInteracts, currentlyViewedStore: store })
  }

  // Pan to the store the user is currently interacting with
  useEffect(() => {
    if (
      map &&
      mapInteracts.currentlyViewedStore &&
      mapInteracts.currentlyViewedStore.address.coordinates
    ) {
      const { coordinates } = mapInteracts.currentlyViewedStore.address
      const isStoreInView = map
        .getBounds()
        ?.contains(new google.maps.LatLng(coordinates.lat, coordinates.lng))

      const mapZoom = map.getZoom() || DEFAULT_MAP_ZOOM
      const mapShouldZoom = mapZoom < 12
      if (!isStoreInView || mapShouldZoom) map.panTo(coordinates)

      if (mapShouldZoom) map.setZoom(12)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInteracts.currentlyViewedStore])

  // Zoom out map to furthest away marker by setting Map bounds
  useEffect(() => {
    if (hasMapLoaded) return
    if (map && storeMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds()
      storeMarkers.forEach(({ store }) => {
        const { coordinates } = store.address
        bounds.extend({ lat: coordinates.lat, lng: coordinates.lng })
      })
      if (currentLocation?.latLng) {
        bounds.extend({
          lat: currentLocation.latLng.lat,
          lng: currentLocation.latLng.lng
        })
      }
      map.fitBounds(bounds)
      setHasMapLoaded(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, storeMarkers, hasMapLoaded])

  return (
    <>
      {currentLocation?.latLng && !!storeMarkers.length && (
        <AdvancedMarker position={currentLocation.latLng} zIndex={11}>
          <UserLocationContainer>
            <UserLocationLabel>Your location</UserLocationLabel>
            <UserLocationMarker>
              <UserLocationIcon />
            </UserLocationMarker>
          </UserLocationContainer>
        </AdvancedMarker>
      )}
      {storeMarkers.map(({ store }) => {
        const { coordinates } = store.address
        const isCurrentlyViewed =
          mapInteracts.currentlyViewedStore?.slug === store.slug
        const isCurrentlyHovered =
          mapInteracts.currentlyHoveredStore?.slug === store.slug
        let markerZIndex = 10
        if (isCurrentlyViewed) markerZIndex = 11
        if (isCurrentlyHovered) markerZIndex = 12
        return (
          <AdvancedMarker
            key={`map-marker-${store.slug}`}
            position={coordinates}
            onClick={() => handleStoreClick(store)}
            onMouseEnter={() =>
              setMapInteracts({ ...mapInteracts, currentlyHoveredStore: store })
            }
            onMouseLeave={() =>
              setMapInteracts({ ...mapInteracts, currentlyHoveredStore: null })
            }
            zIndex={markerZIndex}
          >
            <StoreMarker
              id={`map-marker-${store.slug}`}
              isCurrentlyViewed={isCurrentlyViewed}
              isCurrentlyHovered={isCurrentlyHovered}
            >
              <StoreIcon src={getImage(50, 50)} alt={store.name || 'store'} />
            </StoreMarker>
          </AdvancedMarker>
        )
      })}
    </>
  )
}

const Root = styled.div(() => ({
  width: '100%',
  height: 'inherit'
}))

const UserLocationLabel = styled.div(
  ({ theme }): CSSObject => ({
    display: 'flex',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#fff',
    background: theme.colors.primary,
    padding: '4px 8px'
  })
)

const UserLocationContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px'
  })
)

const UserLocationIcon = styled.div(
  ({ theme }): CSSObject => ({
    position: 'relative',
    zIndex: 10,
    width: '20px',
    height: '20px',
    border: '1px solid #fff',
    background: theme.colors.primary,
    borderRadius: '100%'
  })
)

export default BrowseStoresMap
